/**
 * Petites briques de Mon compte v2 (maquette /msc : .kicker + .linkish, .tag, .notice, .modal).
 * Classes stables préfixées `mmv2-` (SPEC § 6.5), styles dans ../account.css.
 *
 * Les modales sont rendues par Chakra dans un portail À LA RACINE DU DOCUMENT (hors de
 * #mindbody-app : le thème du site isole sa section, une modale rendue dedans resterait sous son
 * en-tête collant). Elles sont donc habillées par le thème Chakra v2 (variante de Modal `mmv2`,
 * variantes de Button `mmv2*`, couleurs `mmv2.*` : valeurs résolues des jetons du site,
 * theme/createThemeV2.ts), pas par account.css.
 */
import {
  Box,
  Button,
  type ButtonProps,
  FormControl,
  FormLabel,
  Input,
  type InputProps,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Text,
  Textarea,
  type TextareaProps,
} from '@chakra-ui/react';
import { Children, Fragment, forwardRef, isValidElement, useCallback, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { brandTagColors, type BrandInfo } from '../lib/brands';
import { Icon, type IconName } from './Icon';

/** Racine d'un écran de Mon compte v2. */
export function AccountShell({ className, children }: { className?: string; children: ReactNode }) {
  return <div className={`mmv2-acct${className ? ` ${className}` : ''}`}>{children}</div>;
}

/** Modale de la maquette (`.overlay` / `.modal`). */
export function AccountModal({
  isOpen,
  onClose,
  title,
  children,
  footer,
  className,
}: {
  isOpen: boolean;
  onClose: () => void;
  title: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  /** Classe en plus de mmv2-acct-modal (ex. mmv2-cancel-modal de la fiche cours). */
  className?: string;
}) {
  const { ref: footerRef, stacked } = useStackedFooter();
  // Empilé : action principale en haut ET en premier dans le DOM (ordre de tabulation = ordre visuel, revue du 05/10).
  const footerItems = flattenFooter(footer);
  if (stacked) footerItems.reverse();
  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered variant="mmv2">
      <ModalOverlay />
      <ModalContent className={`mmv2-acct-modal${className ? ` ${className}` : ''}`} maxW="520px">
        {/* En-tête et croix : variante de Modal « mmv2 » (theme/createThemeV2.ts), croix centrée sur la 1re ligne du titre. */}
        <ModalHeader className="mmv2-acct-modal__title">{title}</ModalHeader>
        <ModalCloseButton aria-label="Fermer" />
        <ModalBody pt="12px" pb="8px" fontSize="15px" lineHeight="1.55">
          {children}
        </ModalBody>
        {footer ? (
          /*
           * Pied commun à toutes les modales v2 (annulation, parrainage, résiliation…), action principale en dernier.
           * Boutons à leur largeur, calés à droite (maquettes 12, 21, 22), tant qu'ils tiennent sur une ligne. Sinon
           * (sous 480 px, ou libellés trop larges : 480 à 552 px, police Eastman des sites Uncode dès 1 440 px), l'un sous
           * l'autre, pleine largeur, principale en haut, le discret en contour pour avoir un bord (revue du 05/10 : pilule
           * au-dessus, « GARDER MA RÉSERVATION » flottant dessous comme un texte, bords décalés). Mesure : useStackedFooter.
           */
          <ModalFooter
            ref={footerRef}
            className="mmv2-acct-modal__footer"
            data-stacked={stacked ? '' : undefined}
            display="flex"
            flexWrap="nowrap"
            justifyContent="flex-end"
            gap="10px"
            pt="14px"
            sx={{
              '& > button': { flex: '0 0 auto', px: '18px', fontSize: '12px', letterSpacing: '0.08em' },
              '&[data-stacked]': {
                flexDirection: 'column',
                alignItems: 'stretch',
                '& > button': { width: '100%' },
                '& > .mmv2-modal-btn--quiet': { boxShadow: 'inset 0 0 0 1.5px var(--chakra-colors-mmv2-line)' },
              },
            }}
          >
            {footerItems}
          </ModalFooter>
        ) : null}
      </ModalContent>
    </Modal>
  );
}

/** Boutons du pied (fragment `<>…</>` des appelants) à plat, avec leurs clés. */
function flattenFooter(footer: ReactNode): ReactNode[] {
  const top = Children.toArray(footer);
  if (top.length === 1 && isValidElement<{ children?: ReactNode }>(top[0]) && top[0].type === Fragment) {
    return Children.toArray(top[0].props.children);
  }
  return top;
}

const NARROW_FOOTER = '(max-width: 479.98px)';

/**
 * Pied empilé quand ses boutons ne tiennent pas côte à côte : somme de leurs largeurs naturelles (max-content, mesurée
 * sans les étirer) + écarts > largeur utile du pied ; toujours sous 480 px. Relu au redimensionnement du pied et quand
 * ses boutons changent (chargement, état « invitation envoyée »).
 */
function useStackedFooter(): { ref: (node: HTMLDivElement | null) => void; stacked: boolean } {
  const [node, setNode] = useState<HTMLDivElement | null>(null);
  const [stacked, setStacked] = useState(false);
  const frame = useRef(0);
  const measure = useCallback(() => {
    if (!node) return;
    const narrow = typeof window.matchMedia === 'function' && window.matchMedia(NARROW_FOOTER).matches;
    const buttons = Array.from(node.children) as HTMLElement[];
    const style = getComputedStyle(node);
    const gap = parseFloat(style.columnGap) || 0;
    const room = node.clientWidth - (parseFloat(style.paddingLeft) || 0) - (parseFloat(style.paddingRight) || 0);
    const needed =
      buttons.reduce((sum, button) => {
        const before = button.style.width;
        button.style.width = 'max-content';
        // Largeur de mise en page (offsetWidth) : la modale s'ouvre avec une mise à l'échelle (transform), que
        // getBoundingClientRect inclurait ; + 1 px pour l'arrondi à l'entier.
        const width = button.offsetWidth + 1;
        button.style.width = before;
        return sum + width;
      }, 0) +
      gap * Math.max(0, buttons.length - 1);
    setStacked(narrow || needed > room + 0.5);
  }, [node]);
  useLayoutEffect(() => {
    if (!node) return undefined;
    measure();
    const schedule = () => {
      cancelAnimationFrame(frame.current);
      frame.current = requestAnimationFrame(measure);
    };
    const resize = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(schedule) : null;
    resize?.observe(node);
    const mutations = typeof MutationObserver !== 'undefined' ? new MutationObserver(schedule) : null;
    mutations?.observe(node, { childList: true, subtree: true, characterData: true });
    window.addEventListener('resize', schedule);
    return () => {
      cancelAnimationFrame(frame.current);
      resize?.disconnect();
      mutations?.disconnect();
      window.removeEventListener('resize', schedule);
    };
  }, [node, measure]);
  return { ref: setNode, stacked };
}

/** Chapô de modale (`.lede` 15,5 px). */
export function ModalLede({ children }: { children: ReactNode }) {
  return (
    <Text m="0 0 16px" fontSize="15.5px" lineHeight="1.6" color="mmv2.ink2" sx={{ textWrap: 'pretty' }}>
      {children}
    </Text>
  );
}

/**
 * Encadré de modale (`.notice`, `.notice.warm`). Information en neutre (comme .mmv2-notice, ux-v2.css) : sur Dynamo,
 * marque et action sont le même rouge, et l'information se lisait comme une alerte.
 */
export function ModalNotice({ tone = 'info', children }: { tone?: 'info' | 'warm'; children: ReactNode }) {
  const warm = tone === 'warm';
  return (
    <Box
      display="flex"
      // Texte sur la verticale de celui des options de la modale d'annulation (bordure 1,5 + 16 + radio 16 + 12 = 45,5 px ;
      // ici 1 + 16 + icône 17 + 12 = 46 px) : 50 px avec 18 et 14 (revue du 05/10).
      gap="12px"
      p="16px 18px 16px 16px"
      border="1px solid"
      borderColor={warm ? 'mmv2.actionBorder' : 'mmv2.line'}
      borderRadius="mmv2Card"
      bg={warm ? 'mmv2.action50' : 'mmv2.sunken'}
      fontSize="13.5px"
      lineHeight="1.6"
      color="mmv2.ink2"
      sx={{ b: { fontWeight: 600, color: 'mmv2.ink' }, textWrap: 'pretty' }}
    >
      <Box as="span" flex="0 0 auto" mt="2px" color={warm ? 'mmv2.actionText' : 'mmv2.ink2'}>
        <Icon name={warm ? 'alert' : 'info'} />
      </Box>
      <Box>{children}</Box>
    </Box>
  );
}

const MODAL_BUTTON_VARIANT = {
  quiet: 'mmv2Quiet',
  dark: 'mmv2Dark',
  primary: 'mmv2Primary',
  danger: 'mmv2Dark',
} as const;

/** Bouton de modale : variantes v2 du thème Chakra (`.btn-quiet`, `.btn-dark`, `.btn-primary`). */
export function ModalButton({
  tone,
  children,
  className,
  ...buttonProps
}: { tone: keyof typeof MODAL_BUTTON_VARIANT; children: ReactNode } & Omit<ButtonProps, 'variant'>) {
  // loadingText ignoré : le libellé reste en place (masqué) sous le spinner, voir plus bas.
  const props = { ...buttonProps, loadingText: undefined };
  // Survol sans effet tant que le bouton est désactivé (chargement : le fond changeait encore, revue du 05/10). Rouge =
  // action (Dynamo) : survol de l'action, redText n'en différait pas assez (1,05:1, revue du 05/10).
  const danger =
    tone === 'danger'
      ? { bg: 'mmv2.red', color: 'white', _hover: { bg: 'mmv2.redHover', _disabled: { bg: 'mmv2.red' } } }
      : {};
  /*
   * Envoi en cours : spinner superposé au libellé masqué (Chakra sans loadingText), le bouton garde sa largeur et le pied
   * ne bouge plus sous le curseur (revue du 05/10 : 222 → 161 px, « GARDER » glissait de 61 px, pied de 2 lignes à 1).
   * Fond plein, sans l'opacité de 0,4 des boutons désactivés (« ANNULATION… » à 2,09:1) ; curseur d'attente.
   */
  const loading = props.isLoading ? { _disabled: { opacity: 1, cursor: 'progress' } } : {};
  return (
    <Button
      variant={MODAL_BUTTON_VARIANT[tone]}
      size="mmv2Md"
      className={[`mmv2-modal-btn--${tone}`, className].filter(Boolean).join(' ')}
      // Libellé accessible du spinner en français (Chakra : « Loading... »).
      // Couleur du texte du bouton (le thème v1 peint ses spinners à la couleur de marque : rouge sur rouge sur Dynamo).
      spinner={<Spinner label="Chargement" size="sm" color="currentColor" />}
      {...danger}
      {...loading}
      {...props}
    >
      {children}
    </Button>
  );
}

/*
 * Champs des modales = .mmv2-input du socle (ux-v2.css) : bordure de 1,5 px au repos à 3:1 (jeton fieldBorder), focus
 * en bordure de marque épaissie (anneau de 1 px) + halo. Revue du 05/10 : bordure de 10 % (1,24:1, « Un petit mot »
 * presque invisible) ramenée à 1 px par la taille Chakra, focus au halo seul.
 */
const fieldStyles = {
  w: '100%',
  border: '1.5px solid',
  borderWidth: '1.5px',
  borderColor: 'mmv2.fieldBorder',
  borderRadius: 'mmv2Input',
  bg: 'mmv2.paper',
  fontFamily: 'mmv2',
  fontSize: '14.5px',
  color: 'mmv2.ink',
  _hover: { borderColor: 'mmv2.ink' },
  _focusVisible: {
    borderColor: 'mmv2.brand',
    boxShadow: '0 0 0 1px var(--chakra-colors-mmv2-brand), 0 0 0 4px var(--chakra-colors-mmv2-brand100)',
    outline: 'none',
  },
  _invalid: { borderColor: 'mmv2.red', boxShadow: 'none' },
  _placeholder: { color: 'mmv2.ink3' },
} as const;

/** Libellé de champ de modale (`.field label`). */
export function ModalLabel({ htmlFor, children }: { htmlFor: string; children: ReactNode }) {
  return (
    <FormLabel
      htmlFor={htmlFor}
      m="0 0 6px"
      fontFamily="mmv2"
      fontSize="10.5px"
      fontWeight={600}
      letterSpacing="0.15em"
      textTransform="uppercase"
      color="mmv2.ink3"
    >
      {children}
    </FormLabel>
  );
}

/** Champ de modale (`.input`). */
export const ModalInput = forwardRef<HTMLInputElement, InputProps>(function ModalInput(props, ref) {
  return <Input ref={ref} h="44px" px="14px" {...fieldStyles} {...props} />;
});

/** Zone de texte de modale. */
export const ModalTextarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function ModalTextarea(props, ref) {
  return (
    <Textarea
      ref={ref}
      minH="92px"
      p="10px 14px"
      lineHeight="1.5"
      resize="vertical"
      {...fieldStyles}
      {...props}
    />
  );
});

/** Groupe champ + libellé de modale. */
export function ModalField({ children, mt = '12px' }: { children: ReactNode; mt?: string }) {
  return <FormControl mt={mt}>{children}</FormControl>;
}

/** Titre de rubrique (micro-typo de la maquette) + lien « Voir tous… » à droite. */
export function SectionHead({
  id,
  title,
  link,
}: {
  id?: string;
  title: string;
  link?: { to: string; label: string };
}) {
  return (
    <div className="mmv2-acct-sechead">
      <h2 className="mmv2-acct-kicker" id={id}>
        {title}
      </h2>
      {link ? (
        <Link className="mmv2-linkish mmv2-acct-seclink" to={link.to}>
          {link.label}
        </Link>
      ) : null}
    </div>
  );
}

export type Tone = 'green' | 'amber' | 'red' | 'neutral' | 'brand' | 'solid';

export function Tag({ tone = 'neutral', children, style }: { tone?: Tone; children: ReactNode; style?: React.CSSProperties }) {
  const cls = tone === 'neutral' ? 'mmv2-tag' : `mmv2-tag mmv2-tag--${tone}`;
  return (
    <span className={cls} style={style}>
      {children}
    </span>
  );
}

/**
 * Étiquette de marque d'un cours (autre marque que le site) : une seule représentation dans Mon compte et Mes cours,
 * teintée de la couleur de la marque (mondayMindbody.uxV2.brands), texte AA ; neutre sans couleur connue (revue du
 * 04/10 : pilule grise, texte gris ou couleur de marque selon l'écran).
 */
export function BrandTag({ brand }: { brand: BrandInfo }) {
  const colors = brandTagColors(brand.color);
  return (
    <span className="mmv2-tag mmv2-acct-brandtag" data-brand={brand.siteId} style={colors ?? undefined}>
      {brand.name}
    </span>
  );
}

/** Encadré de la maquette (`.notice`, `.notice.warm`), plus des variantes d'état. */
export function Notice({
  tone = 'info',
  icon,
  children,
  role,
}: {
  tone?: 'info' | 'warm' | 'success' | 'error';
  icon?: IconName;
  children: ReactNode;
  role?: 'alert' | 'status';
}) {
  const cls =
    tone === 'warm'
      ? 'mmv2-notice mmv2-notice--warm'
      : tone === 'info'
        ? 'mmv2-notice'
        : `mmv2-notice mmv2-acct-notice--${tone}`;
  const fallbackIcon: IconName = tone === 'success' ? 'check' : tone === 'info' ? 'info' : 'alert';
  return (
    <div className={cls} role={role}>
      <Icon name={icon ?? fallbackIcon} />
      <div>{children}</div>
    </div>
  );
}

/** « ← Retour à mon compte » (`.btn-quiet.btn-sm` de la maquette). */
export function BackLink({ to = '/account', label = 'Retour à mon compte' }: { to?: string; label?: string }) {
  return (
    <Link className="mmv2-btn mmv2-btn--quiet mmv2-btn--sm mmv2-acct-back" to={to}>
      <Icon name="left" size={14} />
      {label}
    </Link>
  );
}

/** Note de bas de bloc (`.tiny.muted` + icône info). */
export function FootNote({ children }: { children: ReactNode }) {
  return (
    <p className="mmv2-acct-footnote">
      <Icon name="info" size={12} />
      <span>{children}</span>
    </p>
  );
}

/**
 * Lecture en échec, rien en cache (GET client/visits en erreur ou en pause hors ligne ; relecture du 09/10 de la décision
 * de DA sur Mes chiffres) : « <children> pour le moment », message neutre À LA PLACE EXACTE du squelette de la rubrique (même boîte, account.css
 * .mmv2-acct-unavailable), au lieu de « Rien de réservé pour l'instant » ou « Aucun cours passé pour l'instant »
 * (fausse information) : ni saut entre l'attente et l'erreur, ni bouton (« Réessayer » de Mes chiffres relit les
 * visites de toutes les rubriques à la fois).
 */
export function UnavailableBlock({ children }: { children: ReactNode }) {
  // « pour le moment » d'un seul tenant (account.css) : sur une ligne étroite, la coupure tombe devant, comme dans la
  // carte de Mes chiffres (« Chiffres indisponibles » / « pour le moment »), jamais devant « moment » seul.
  return (
    <div className="mmv2-acct-unavailable">
      <p className="mmv2-muted">
        {children} <span className="mmv2-acct-unavailable__when">pour le moment</span>
      </p>
    </div>
  );
}
